import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

// Helper to get current date in DD/MM/YYYY format
const getCurrentDate = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function IPRProtectionTabEditor({ data, onChange }) {
  const [activeSection, setActiveSection] = useState('patents_filed');

  if (!data) return null;

  const updateSection = (section, value) => {
    onChange({ ...data, [section]: value });
  };

  // Patents Filed
  const addPatentFiled = () => {
    const newList = [...(data.patents_filed || []), {
      id: uuidv4(),
      patent_file_no: '',
      technology: '',
      patent_file_date: getCurrentDate(),
      status: '',
      file: null
    }];
    updateSection('patents_filed', newList);
  };

  const updatePatentFiled = (index, field, value) => {
    const newList = [...data.patents_filed];
    newList[index] = { ...newList[index], [field]: value };
    updateSection('patents_filed', newList);
  };

  const removePatentFiled = (index) => {
    const newList = data.patents_filed.filter((_, i) => i !== index);
    updateSection('patents_filed', newList);
  };

  // Patents Granted
  const addPatentGranted = () => {
    const newList = [...(data.patents_granted || []), {
      id: uuidv4(),
      registration_no: '',
      technology: '',
      grant_date: getCurrentDate(),
      certificate: null
    }];
    updateSection('patents_granted', newList);
  };

  const updatePatentGranted = (index, field, value) => {
    const newList = [...data.patents_granted];
    newList[index] = { ...newList[index], [field]: value };
    updateSection('patents_granted', newList);
  };

  const removePatentGranted = (index) => {
    const newList = data.patents_granted.filter((_, i) => i !== index);
    updateSection('patents_granted', newList);
  };

  // Copyrights Filed
  const addCopyrightFiled = () => {
    const newList = [...(data.copyrights_filed || []), {
      id: uuidv4(),
      diary_no: '',
      mobile_app: '',
      filing_date: getCurrentDate(),
      application_status: '',
      file: null
    }];
    updateSection('copyrights_filed', newList);
  };

  const updateCopyrightFiled = (index, field, value) => {
    const newList = [...data.copyrights_filed];
    newList[index] = { ...newList[index], [field]: value };
    updateSection('copyrights_filed', newList);
  };

  const removeCopyrightFiled = (index) => {
    const newList = data.copyrights_filed.filter((_, i) => i !== index);
    updateSection('copyrights_filed', newList);
  };

  // Copyrights Granted
  const addCopyrightGranted = () => {
    const newList = [...(data.copyrights_granted || []), {
      id: uuidv4(),
      diary_no: '',
      mobile_app: '',
      grant_date: getCurrentDate(),
      certificate: null
    }];
    updateSection('copyrights_granted', newList);
  };

  const updateCopyrightGranted = (index, field, value) => {
    const newList = [...data.copyrights_granted];
    newList[index] = { ...newList[index], [field]: value };
    updateSection('copyrights_granted', newList);
  };

  const removeCopyrightGranted = (index) => {
    const newList = data.copyrights_granted.filter((_, i) => i !== index);
    updateSection('copyrights_granted', newList);
  };

  // Geographical Indications
  const addGI = () => {
    const newList = [...(data.geographical_indications || []), {
      id: uuidv4(),
      registration_no: '',
      gi_name: '',
      grant_date: getCurrentDate(),
      certificate: null
    }];
    updateSection('geographical_indications', newList);
  };

  const updateGI = (index, field, value) => {
    const newList = [...data.geographical_indications];
    newList[index] = { ...newList[index], [field]: value };
    updateSection('geographical_indications', newList);
  };

  const removeGI = (index) => {
    const newList = data.geographical_indications.filter((_, i) => i !== index);
    updateSection('geographical_indications', newList);
  };

  const sections = [
    { key: 'patents_filed', label: 'Patents Filed' },
    { key: 'patents_granted', label: 'Patents Granted' },
    { key: 'copyrights_filed', label: 'Copyrights Filed' },
    { key: 'copyrights_granted', label: 'Copyrights Granted' },
    { key: 'geographical_indications', label: 'Geographical Indications' }
  ];

  return (
    <div className="space-y-6">
      {/* Section Selector */}
      <div className="bg-white p-4 rounded-lg border">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Section to Edit
        </label>
        <div className="flex flex-wrap gap-2">
          {sections.map(section => (
            <Button
              key={section.key}
              onClick={() => setActiveSection(section.key)}
              variant={activeSection === section.key ? 'default' : 'outline'}
              size="sm"
              className={activeSection === section.key ? 'bg-[#0E6D2B]' : ''}
            >
              {section.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Patents Filed */}
      {activeSection === 'patents_filed' && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Patents Filed</h3>
            <Button onClick={addPatentFiled} size="sm">
              <Plus className="w-4 h-4 mr-1" /> Add Patent
            </Button>
          </div>
          <div className="space-y-4">
            {data.patents_filed && data.patents_filed.map((patent, index) => (
              <div key={patent.id} className="border rounded p-4 bg-gray-50">
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Patent File No.</label>
                    <Input
                      value={patent.patent_file_no}
                      onChange={(e) => updatePatentFiled(index, 'patent_file_no', e.target.value)}
                      placeholder="202411012345"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Date (DD/MM/YYYY)</label>
                    <Input
                      value={patent.patent_file_date}
                      onChange={(e) => updatePatentFiled(index, 'patent_file_date', e.target.value)}
                      placeholder="01/01/2024"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-600 mb-1 block">Technology</label>
                  <Input
                    value={patent.technology}
                    onChange={(e) => updatePatentFiled(index, 'technology', e.target.value)}
                    placeholder="Technology description"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Status</label>
                    <Input
                      value={patent.status}
                      onChange={(e) => updatePatentFiled(index, 'status', e.target.value)}
                      placeholder="Under examination"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">File Path (optional)</label>
                    <Input
                      value={patent.file || ''}
                      onChange={(e) => updatePatentFiled(index, 'file', e.target.value)}
                      placeholder="/api/files/download/..."
                    />
                  </div>
                </div>
                <Button onClick={() => removePatentFiled(index)} size="sm" variant="destructive">
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Patents Granted */}
      {activeSection === 'patents_granted' && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Patents Granted</h3>
            <Button onClick={addPatentGranted} size="sm">
              <Plus className="w-4 h-4 mr-1" /> Add Patent
            </Button>
          </div>
          <div className="space-y-4">
            {data.patents_granted && data.patents_granted.map((patent, index) => (
              <div key={patent.id} className="border rounded p-4 bg-gray-50">
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Registration No.</label>
                    <Input
                      value={patent.registration_no}
                      onChange={(e) => updatePatentGranted(index, 'registration_no', e.target.value)}
                      placeholder="IN 345678"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Grant Date (DD/MM/YYYY)</label>
                    <Input
                      value={patent.grant_date}
                      onChange={(e) => updatePatentGranted(index, 'grant_date', e.target.value)}
                      placeholder="01/01/2023"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-600 mb-1 block">Technology</label>
                  <Input
                    value={patent.technology}
                    onChange={(e) => updatePatentGranted(index, 'technology', e.target.value)}
                    placeholder="Technology description"
                  />
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-600 mb-1 block">Certificate Path (optional)</label>
                  <Input
                    value={patent.certificate || ''}
                    onChange={(e) => updatePatentGranted(index, 'certificate', e.target.value)}
                    placeholder="/api/files/download/..."
                  />
                </div>
                <Button onClick={() => removePatentGranted(index)} size="sm" variant="destructive">
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Copyrights Filed */}
      {activeSection === 'copyrights_filed' && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Copyrights Filed</h3>
            <Button onClick={addCopyrightFiled} size="sm">
              <Plus className="w-4 h-4 mr-1" /> Add Copyright
            </Button>
          </div>
          <div className="space-y-4">
            {data.copyrights_filed && data.copyrights_filed.map((copyright, index) => (
              <div key={copyright.id} className="border rounded p-4 bg-gray-50">
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Diary No.</label>
                    <Input
                      value={copyright.diary_no}
                      onChange={(e) => updateCopyrightFiled(index, 'diary_no', e.target.value)}
                      placeholder="L-12345/2024"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Filing Date (DD/MM/YYYY)</label>
                    <Input
                      value={copyright.filing_date}
                      onChange={(e) => updateCopyrightFiled(index, 'filing_date', e.target.value)}
                      placeholder="01/01/2024"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-600 mb-1 block">Mobile App / Software</label>
                  <Input
                    value={copyright.mobile_app}
                    onChange={(e) => updateCopyrightFiled(index, 'mobile_app', e.target.value)}
                    placeholder="App name"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Application Status</label>
                    <Input
                      value={copyright.application_status}
                      onChange={(e) => updateCopyrightFiled(index, 'application_status', e.target.value)}
                      placeholder="Pending"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">File Path (optional)</label>
                    <Input
                      value={copyright.file || ''}
                      onChange={(e) => updateCopyrightFiled(index, 'file', e.target.value)}
                      placeholder="/api/files/download/..."
                    />
                  </div>
                </div>
                <Button onClick={() => removeCopyrightFiled(index)} size="sm" variant="destructive">
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Copyrights Granted */}
      {activeSection === 'copyrights_granted' && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Copyrights Granted</h3>
            <Button onClick={addCopyrightGranted} size="sm">
              <Plus className="w-4 h-4 mr-1" /> Add Copyright
            </Button>
          </div>
          <div className="space-y-4">
            {data.copyrights_granted && data.copyrights_granted.map((copyright, index) => (
              <div key={copyright.id} className="border rounded p-4 bg-gray-50">
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Diary No.</label>
                    <Input
                      value={copyright.diary_no}
                      onChange={(e) => updateCopyrightGranted(index, 'diary_no', e.target.value)}
                      placeholder="L-98765/2022"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Grant Date (DD/MM/YYYY)</label>
                    <Input
                      value={copyright.grant_date}
                      onChange={(e) => updateCopyrightGranted(index, 'grant_date', e.target.value)}
                      placeholder="01/01/2023"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-600 mb-1 block">Mobile App / Software</label>
                  <Input
                    value={copyright.mobile_app}
                    onChange={(e) => updateCopyrightGranted(index, 'mobile_app', e.target.value)}
                    placeholder="App name"
                  />
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-600 mb-1 block">Certificate Path (optional)</label>
                  <Input
                    value={copyright.certificate || ''}
                    onChange={(e) => updateCopyrightGranted(index, 'certificate', e.target.value)}
                    placeholder="/api/files/download/..."
                  />
                </div>
                <Button onClick={() => removeCopyrightGranted(index)} size="sm" variant="destructive">
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Geographical Indications */}
      {activeSection === 'geographical_indications' && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Geographical Indications (GI)</h3>
            <Button onClick={addGI} size="sm">
              <Plus className="w-4 h-4 mr-1" /> Add GI
            </Button>
          </div>
          <div className="space-y-4">
            {data.geographical_indications && data.geographical_indications.map((gi, index) => (
              <div key={gi.id} className="border rounded p-4 bg-gray-50">
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Registration No.</label>
                    <Input
                      value={gi.registration_no}
                      onChange={(e) => updateGI(index, 'registration_no', e.target.value)}
                      placeholder="GI-456789"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Grant Date (DD/MM/YYYY)</label>
                    <Input
                      value={gi.grant_date}
                      onChange={(e) => updateGI(index, 'grant_date', e.target.value)}
                      placeholder="01/01/2022"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-600 mb-1 block">GI Name</label>
                  <Input
                    value={gi.gi_name}
                    onChange={(e) => updateGI(index, 'gi_name', e.target.value)}
                    placeholder="Lucknow Dashehari Mango"
                  />
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-600 mb-1 block">Certificate Path (optional)</label>
                  <Input
                    value={gi.certificate || ''}
                    onChange={(e) => updateGI(index, 'certificate', e.target.value)}
                    placeholder="/api/files/download/..."
                  />
                </div>
                <Button onClick={() => removeGI(index)} size="sm" variant="destructive">
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
